import { isBoolean, isInteger, isObject, isString } from "lodash-es";
import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";

import { userSettingChanged } from "@/analytics/events/other";
import { trackEvent, trackUserProperty } from "@/analytics/track";
import { sendSetting } from "@/composables/useEventBus";
import type { CfdGroupingMode, WorkUnit } from "@/model/analytics";
import type { WorkingMode } from "@/model/baseTypes";
import { assureBoardType } from "@/model/board";
import type { PivotDimensionId } from "@/model/pivot";
import { DEFAULT_PIVOT_COL_DIM, DEFAULT_PIVOT_ROW_DIM } from "@/model/pivot";
import type { Session } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import { storesLen } from "@/storesPlugin";
import { LocalStore } from "@/utils/LocalStore";
import { keysIn } from "@/utils/record";

import { useBoardStore } from "./board";
import { useSessionStore } from "./session";
import { statsProvider } from "./statistics";

export const fonts = ["Manrope", "Caveat", "Natural", "Child", "Lefthand", "Bedtime"] as const;

function isFont(name: unknown): name is (typeof fonts)[number] {
  return isString(name) && fonts.includes(name);
}

export type StickyNoteTextAlignment = "left" | "center" | "right";
type InputDevice = "mouse" | "trackpad" | "auto";
type EffectiveInputDevice = Exclude<InputDevice, "auto">;

function isInputDevice(value: unknown): value is InputDevice {
  return isString(value) && ["mouse", "trackpad", "auto"].includes(value);
}

function isEffectiveInputDevice(value: unknown): value is EffectiveInputDevice {
  return isString(value) && ["mouse", "trackpad"].includes(value);
}

export const textAlignmentOptions: Array<{ value: StickyNoteTextAlignment; label: string }> = [
  { value: "left", label: /*$t*/ "settings.stickyNoteTextAlignment.left" },
  { value: "center", label: /*$t*/ "settings.stickyNoteTextAlignment.center" },
  { value: "right", label: /*$t*/ "settings.stickyNoteTextAlignment.right" },
];

function isAlignment(value: unknown): value is StickyNoteTextAlignment {
  return textAlignmentOptions.some((option) => option.value === value);
}

interface ChartDateRange {
  start?: string | null;
  end?: string | null;
}

export interface ChartPreferences {
  workUnit?: WorkUnit;
  groupingMode?: CfdGroupingMode;
  hideNonWorkingDays?: boolean;
  dateRanges?: Record<string, ChartDateRange>;
}

interface MapViewPreferences {
  grouped: boolean;
  row: PivotDimensionId;
  col: PivotDimensionId;
}

function isMapView(value: unknown): value is MapViewPreferences {
  return (
    isObject(value) &&
    "grouped" in value &&
    isBoolean(value.grouped) &&
    "row" in value &&
    isString(value.row) &&
    "col" in value &&
    isString(value.col)
  );
}

// a feature highlight: the local dates it was shown on, and whether the item was used
interface SeenHighlight {
  days: string[];
  used: boolean;
}

export interface ClientSettings {
  workingModes: Record<Session["id"], WorkingMode>;
  stickyFont: (typeof fonts)[number];
  inputDevice: InputDevice; // replaces wheelZoom
  effectiveInputDevice: EffectiveInputDevice;
  overload: boolean;
  permanentTeamRiskCanvasLinks: boolean;
  permanentProgramLinks: boolean;
  highlightDoneLinks: boolean;
  initialLineupDistance: number;
  stackDistanceX: number;
  stackDistanceY: number;
  showStats: number;
  overviewScale: number;
  defaultStickyTypes: Record<string, string>;
  highContrastMode: boolean;
  panelWidths: Record<string, number>;
  artObjectivesPanelOpen: boolean;
  textAlignment: StickyNoteTextAlignment;
  archivedSessions: boolean;
  chartPreferences: Record<string, ChartPreferences>;
  mapView: MapViewPreferences;
  seenHighlights: Record<string, SeenHighlight>;
}

type Setting = keyof ClientSettings;

export const useClientSettingsStore = defineStore("clientSettings", () => {
  const state = reactive(loadSettings());

  const defaultStickyTypeKey = computed(() => {
    const sessionId = useSessionStore().current.id;
    const board = useBoardStore().currentBoard();
    const flexType = assureBoardType(board, "flex")?.flexType.id || "";
    return `${sessionId}-${board.type}-${flexType}`;
  });

  function chartPreference(chartId: string): ChartPreferences {
    return state.chartPreferences[chartId] ?? {};
  }

  function setDefaultStickyType(type?: StickyType) {
    trackEvent(userSettingChanged({ key: "defaultStickyType", toValue: type?.functionality }));
    if (type) {
      state.defaultStickyTypes[defaultStickyTypeKey.value] = type.id;
    } else {
      delete state.defaultStickyTypes[defaultStickyTypeKey.value];
    }
  }

  function setWorkingMode(mode: WorkingMode) {
    const sessionId = useSessionStore().current.id;
    state.workingModes[sessionId] = mode;
    trackUserProperty("workingMode", mode);
  }

  function setInputDevice(device: InputDevice) {
    if (state.inputDevice !== device) {
      state.inputDevice = device;
      if (device !== "auto") {
        setEffectiveInputDevice(device);
      }
    }
  }

  function setEffectiveInputDevice(device: EffectiveInputDevice) {
    if (state.effectiveInputDevice !== device) {
      state.effectiveInputDevice = device;
    }
  }

  function seenHighlight(id: string) {
    return (state.seenHighlights[id] ??= { days: [], used: false });
  }

  return {
    ...toRefs(state),

    defaultStickyTypeId: computed(() => state.defaultStickyTypes[defaultStickyTypeKey.value]),
    workingMode: computed(() => state.workingModes[useSessionStore().current.id] || "planning"),
    panelWidth: (name: string) => state.panelWidths[name],
    chartPreference,

    init: () => useClientSettingsStore().$subscribe((_, current) => saveSettings(current)),
    $reset: () => Object.assign(state, loadSettings()),
    toggleShowStats: (level: number) => {
      state.showStats = state.showStats !== level ? level : 0;
      setStoresLenStatsProvider(state.showStats);
    },
    toggleArchivedSessions: () => (state.archivedSessions = !state.archivedSessions),
    setDefaultStickyType,
    setPanelWidth: (name: string, width: number) => (state.panelWidths[name] = width),
    setArtObjectivesPanelOpen: (open: boolean) => (state.artObjectivesPanelOpen = open),
    setMapView: (prefs: Partial<MapViewPreferences>) =>
      (state.mapView = { ...state.mapView, ...prefs }),
    setChartPreference: (chartId: string, prefs: Partial<ChartPreferences>) =>
      (state.chartPreferences[chartId] = { ...chartPreference(chartId), ...prefs }),
    recordHighlightDay: (id: string, day: string) => {
      const seen = seenHighlight(id);
      if (!seen.days.includes(day)) seen.days.push(day);
    },
    markHighlightUsed: (id: string) => (seenHighlight(id).used = true),
    setWorkingMode,
    setInputDevice,
    setEffectiveInputDevice,
  };
});

const settings: { [K in Setting]: LocalStore<ClientSettings[K]> } = {
  workingModes: store("workingModes", {}, isObject), // Working mode set per-session
  stickyFont: store<(typeof fonts)[number]>("stickyFont", "Manrope", isFont),
  inputDevice: store<InputDevice>("inputDevice", "auto", isInputDevice),
  effectiveInputDevice: store<EffectiveInputDevice>(
    "effectiveInputDevice",
    "mouse",
    isEffectiveInputDevice,
  ),
  overload: store("overload", true, isBoolean),
  permanentTeamRiskCanvasLinks: store("permanentTeamRiskCanvasLinks", false, isBoolean),
  permanentProgramLinks: store("permanentProgramLinks", true, isBoolean),
  highlightDoneLinks: store("highlightDoneLinks", true, isBoolean),
  initialLineupDistance: store("initialLineupDistance", 110, isInteger),
  stackDistanceX: store("stackDistanceX", 15, isInteger),
  stackDistanceY: store("stackDistanceY", 15, isInteger),
  showStats: store("showStats", 0, isInteger),
  overviewScale: store("overviewScale", 6, isInteger),
  defaultStickyTypes: store("defaultStickyTypes", {}, isObject),
  // To meet accessibility requirements
  highContrastMode: store("highContrastMode", false, isBoolean),
  panelWidths: store("panelWidths", {}, isObject),
  artObjectivesPanelOpen: store("artObjectivesPanelOpen", true, isBoolean),
  chartPreferences: store("chartPreferences", {}, isObject),
  mapView: store<MapViewPreferences>(
    "mapView",
    { grouped: false, row: DEFAULT_PIVOT_ROW_DIM, col: DEFAULT_PIVOT_COL_DIM },
    isMapView,
  ),
  textAlignment: store<StickyNoteTextAlignment>("textAlignment", "left", isAlignment),
  archivedSessions: store("archivedSessions", false, isBoolean),
  seenHighlights: store("seenHighlights", {}, isObject),
};

function loadSettings(): ClientSettings {
  const res = {} as ClientSettings;
  for (const prop of keysIn(settings)) {
    loadSetting(res, prop);
  }
  initEffectiveInputDevice(res);
  setStoresLenStatsProvider(res.showStats);
  return res;
}

function saveSettings(data: ClientSettings) {
  for (const prop of keysIn(settings)) {
    saveSetting(data, prop);
  }
}

function loadSetting<T extends Setting>(data: ClientSettings, prop: T) {
  data[prop] = settings[prop].load()!;
}

function saveSetting<T extends Setting>(data: ClientSettings, prop: T) {
  settings[prop].save(data[prop]);
}

/**
 * Handles any mismatches between inputDevice (what the user selects)
 * and effectiveInputDevice (what gets applied).
 */
function initEffectiveInputDevice(state: ClientSettings) {
  const device = state.inputDevice;
  if (device === "mouse" || device === "trackpad") {
    state.effectiveInputDevice = device;
  }
}
function setStoresLenStatsProvider(showStats: number) {
  statsProvider.storesLen = showStats === 0 ? () => 0 : () => storesLen(["statistics"]);
}

function store<T>(key: string, defaultVal: T, valid: (data: unknown) => boolean) {
  const store: LocalStore<T> = new LocalStore<T>(key, {
    sedes: {
      serialize: (value) => {
        sendSetting(key, value);
        return value;
      },
      deserialize: (data: unknown): T | undefined => {
        if (valid(data)) {
          return data as T;
        }
        store.save(defaultVal);
        return defaultVal;
      },
    },
  });
  return store;
}
